import { useState } from "react";
import { postContact } from "@/lib/api";
import { POSTAL_ADDRESS } from "@/lib/constants";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    try {
      await postContact(form);
      setDone(true);
      toast.success("Message received.");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      {/* HERO */}
      <section className="border-b border-hairline">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 pt-20 md:pt-32 pb-16 md:pb-20">
          <div className="label-mono text-muted">Contact</div>
          <h1 className="font-display mt-8 text-[2.5rem] sm:text-5xl md:text-7xl lg:text-[5.5rem] leading-[1.02] tracking-tight max-w-5xl">
            Say something<br/>useful.
          </h1>
          <p className="font-display-medium text-2xl md:text-4xl mt-8 max-w-4xl">
            Press, interviews, partnerships, or a thoughtful push-back: it lands directly in my inbox.
          </p>
        </div>
      </section>

      {/* DETAILS + FORM */}
      <section>
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* DETAILS */}
          <div className="md:col-span-5 space-y-12" data-testid="contact-details">
            <div>
              <div className="label-mono text-muted">Email</div>
              <a
                href="mailto:hello@thebiologytrader.com"
                className="mt-3 block font-display text-2xl md:text-3xl text-ink hover:text-leaf transition-colors"
                data-testid="contact-email-link"
              >
                hello@thebiologytrader.com
              </a>
            </div>

            <div>
              <div className="label-mono text-muted">Website</div>
              <a
                href="https://thebiologytrader.com"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 block font-display text-2xl md:text-3xl text-ink hover:text-leaf transition-colors"
                data-testid="contact-website-link"
              >
                TheBiologyTrader.com
              </a>
            </div>

            <div>
              <div className="label-mono text-muted">Company</div>
              <address className="mt-3 not-italic font-display text-xl md:text-2xl text-ink leading-snug">
                Biology Trader Education LLC<br/>
                {POSTAL_ADDRESS}
              </address>
            </div>
          </div>

          {/* FORM */}
          <div className="md:col-span-6 md:col-start-7">
            {done ? (
              <div
                className="border-t border-b border-hairline py-16 text-center"
                data-testid="contact-success"
              >
                <div className="label-mono text-leaf">Sent</div>
                <p className="font-display text-3xl md:text-4xl mt-6 leading-tight">
                  Message received.
                </p>
                <p className="text-muted mt-6">I’ll get back to you within a few days.</p>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-10">
                <div>
                  <label className="label-mono text-muted block mb-2">Name</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={set("name")}
                    className="w-full bg-transparent border-b border-hairline focus:border-ink py-3 text-lg outline-none transition-colors placeholder:text-muted/60"
                    placeholder="Your name"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="label-mono text-muted block mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    className="w-full bg-transparent border-b border-hairline focus:border-ink py-3 text-lg outline-none transition-colors placeholder:text-muted/60"
                    placeholder="you@email.com"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="label-mono text-muted block mb-2">Message</label>
                  <textarea
                    required
                    value={form.message}
                    onChange={set("message")}
                    rows={6}
                    className="w-full bg-transparent border-b border-hairline focus:border-ink py-3 text-lg outline-none transition-colors placeholder:text-muted/60 resize-none"
                    placeholder="Questions, feedback or insights…"
                    data-testid="contact-message-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="group inline-flex items-center gap-3 bg-ink text-white px-8 py-4 label-mono hover:bg-leaf transition-colors disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {loading ? "Sending…" : "Send message"}
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
